import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, MapPin, MessageCircle } from "lucide-react";
import { api, extractError } from "@/lib/api";
import { useI18n } from "@/contexts/LanguageContext";
import { optionLabel } from "@/lib/profileLabels";
import OverflowMenu from "@/components/OverflowMenu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
function photoUrl(u) {
  if (!u) return null;
  return u.startsWith("http") ? u : `${BACKEND_URL}${u}`;
}

function Chips({ list, kind, lang }) {
  if (!list?.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {list.map((k) => (
        <span key={k} className="px-3 py-1 rounded-full text-xs border"
          style={{ borderColor: "hsl(var(--nvl-border))" }}>
          {optionLabel(lang, kind, k)}
        </span>
      ))}
    </div>
  );
}

export default function MatchDetail() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/matches/${matchId}`);
        setData(data);
      } catch (e) {
        setError(extractError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [matchId]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto" data-testid="match-detail-loading">
        <div className="nvl-card p-6 animate-pulse text-sm">
          {t("common.loading")}
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-3xl mx-auto" data-testid="match-detail-error">
        <button onClick={() => navigate(-1)} className="text-xs underline mb-4 flex items-center">
          <ArrowLeft size={14} className="mr-1" /> {t("app.discovery.back")}
        </button>
        <div className="nvl-card p-6 text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</div>
      </div>
    );
  }
  if (!data) return null;

  const p = data.profile || {};
  const compat = data.compatibility || {};
  const photos = p.photos || [];
  const mainPhoto = photos.find((x) => x.is_main) || photos[0];
  const mainUrl = photoUrl(mainPhoto?.url);

  return (
    <div className="max-w-3xl mx-auto" data-testid="match-detail-page">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => navigate(-1)} className="text-xs underline flex items-center" data-testid="match-detail-back">
          <ArrowLeft size={14} className="mr-1" /> {t("app.discovery.back")}
        </button>
        <OverflowMenu
          userId={p.user_id}
          contextMatchId={matchId}
          onBlocked={() => navigate("/app/matches")}
          testIdPrefix="match-overflow"
        />
      </div>

      <div className="nvl-card overflow-hidden">
        <div
          className="relative aspect-[4/5] md:aspect-[16/9] w-full flex items-end p-8"
          style={{
            backgroundImage: mainUrl ? `url(${mainUrl})` : undefined,
            backgroundSize: "cover", backgroundPosition: "center",
            background: mainUrl ? undefined : "linear-gradient(135deg, hsl(6 43% 46% / 0.25), hsl(223 66% 10% / 0.95))",
            color: "hsl(var(--nvl-bg))",
          }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: mainUrl ? "linear-gradient(180deg, transparent 40%, rgba(10,17,40,0.85))" : undefined }}
          />
          <div className="relative w-full flex items-end justify-between gap-4">
            <div>
              {p.verification_status === "verified" ? (
                <span
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest mb-3"
                  style={{ backgroundColor: "hsl(var(--nvl-accent))" }}
                >
                  <ShieldCheck size={12} /> {t("app.discovery.verified")}
                </span>
              ) : null}
              <h1 className="font-display text-3xl md:text-4xl leading-tight">
                {p.display_name}{p.age ? <span className="opacity-80">, {p.age}</span> : null}
              </h1>
              {p.city ? (
                <p className="mt-2 text-sm flex items-center gap-2 opacity-90">
                  <MapPin size={14} /> {p.city}{p.country ? `, ${p.country}` : ""}
                  {typeof p.distance_km === "number" ? ` · ${Math.round(p.distance_km)} ${t("app.discovery.km")}` : ""}
                </p>
              ) : null}
            </div>
            {compat.confidence !== "low" ? (
              <div className="text-right">
                <div className="font-display text-3xl md:text-4xl leading-none" data-testid="match-detail-compat">
                  {compat.score}%
                </div>
                <div className="text-[10px] uppercase tracking-widest opacity-90 mt-1">
                  {t(`app.discovery.category.${compat.category}`)}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="p-6 space-y-6">
          {compat.shared_top_values?.length ? (
            <div>
              <p className="nvl-overline mb-2" style={{ color: "hsl(var(--nvl-accent))" }}>
                {t("app.discovery.sharedTopValues")}
              </p>
              <Chips list={compat.shared_top_values} kind="values" lang={lang} />
            </div>
          ) : null}
          {compat.shared_interests?.length ? (
            <div>
              <p className="nvl-overline mb-2" style={{ color: "hsl(var(--nvl-accent))" }}>
                {t("app.discovery.sharedInterests")}
              </p>
              <Chips list={compat.shared_interests} kind="interests" lang={lang} />
            </div>
          ) : null}
          {compat.shared_goals?.length ? (
            <div>
              <p className="nvl-overline mb-2" style={{ color: "hsl(var(--nvl-accent))" }}>
                {t("app.discovery.sharedGoals")}
              </p>
              <Chips list={compat.shared_goals} kind="relationship_goals" lang={lang} />
            </div>
          ) : null}

          <div className="flex gap-3 flex-wrap pt-2">
            <Link
              to={`/app/messages/${matchId}`}
              className="nvl-btn nvl-btn-primary flex-1"
              data-testid="match-send-message"
            >
              <MessageCircle size={14} className="mr-2" />
              {t("app.match.sendMessage")}
            </Link>
            <Link
              to={`/app/discover/${p.user_id}`}
              className="nvl-btn nvl-btn-outline flex-1"
              data-testid="match-view-profile"
            >
              {t("app.match.viewProfile")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
